import { useState, useMemo } from 'react';
import { MFZ_GAMES, MFZ_CATEGORIES, MFZ_COMMENTS, FEATURED_GAME_ID, DAILY_GAME_ID } from '../../data/megafunzoneGames';
import { BANNER_ADS } from '../parody-data.js';
import RufflePlayer from '../apps/RufflePlayer';

const StarRating = ({ rating }) => {
  const full  = Math.floor(rating);
  const empty = 5 - full;
  return (
    <span className="mfz-stars">
      {'★'.repeat(full)}{'☆'.repeat(empty)}
    </span>
  );
};

const GameThumbnail = ({ game }) => (
  <div className="mfz-thumb" style={{ background: game.thumbBg }}>
    <span className="mfz-thumb-label">{game.thumbLabel}</span>
    {game.isNew     && <span className="mfz-new-badge">NEW</span>}
    {game.staffPick && <span className="mfz-staff-badge">STAFF</span>}
  </div>
);

const MFZFooter = ({ onNavigate }) => (
  <div className="mfz-footer">
    <div className="mfz-footer-links">
      <a href="#">Home</a> | <a href="#">About Us</a> | <a href="#">Contact</a> | <a href="#">Advertise</a> |{' '}
      <a href="#">Submit a Game</a> | <a href="#">Privacy Policy</a> | <a href="#">Terms of Service</a> |{' '}
      <a href="#">Help / FAQ</a>
    </div>
    <p>© 2007 MegaFunZone.com — Play Flash Games Online For Free. A Profit Dynamics International™ Distraction Platform. All Rights Reserved.</p>
    <p>
      This site requires Adobe Flash Player 9.0 or higher.{' '}
      <a href="#">Download Flash Player</a> — Not responsible for lost productivity, failed exams, or damaged relationships.
    </p>
    <button className="mfz-back-btn" onClick={() => onNavigate('portal')}>
      &lt;&lt; Back to Portal
    </button>
  </div>
);

export const MegaFunZonePage = ({ onNavigate }) => {
  const [view,           setView]           = useState('home');
  const [selectedGame,   setSelectedGame]   = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy,         setSortBy]         = useState('popular');

  const featuredGame = MFZ_GAMES.find(g => g.id === FEATURED_GAME_ID);
  const dailyGame    = MFZ_GAMES.find(g => g.id === DAILY_GAME_ID);

  const randomAd = useMemo(
    () => BANNER_ADS[Math.floor(Math.random() * BANNER_ADS.length)],
    []
  );

  const filteredGames = useMemo(() => {
    let games = activeCategory === 'All'
      ? MFZ_GAMES
      : MFZ_GAMES.filter(g => g.category === activeCategory);
    if (sortBy === 'popular') return [...games].sort((a, b) => parseInt(b.plays.replace(/,/g, '')) - parseInt(a.plays.replace(/,/g, '')));
    if (sortBy === 'rating')  return [...games].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'newest')  return [...games].sort((a, b) => b.year - a.year);
    return games;
  }, [activeCategory, sortBy]);

  const topRated = useMemo(
    () => [...MFZ_GAMES].sort((a, b) => b.rating - a.rating).slice(0, 6),
    []
  );

  const relatedGames = selectedGame
    ? MFZ_GAMES.filter(g => g.category === selectedGame.category && g.id !== selectedGame.id).slice(0, 4)
    : [];

  const gameComments = selectedGame
    ? (MFZ_COMMENTS[selectedGame.id] || MFZ_COMMENTS.default)
    : [];

  const openGame = (game) => {
    setSelectedGame(game);
    setView('game');
  };

  const goHome = () => {
    setView('home');
    setSelectedGame(null);
  };

  const Header = () => (
    <div className="mfz-header">
      <div className="mfz-header-top">
        <div className="mfz-logo" onClick={goHome}>
          <span className="logo-mega">MEGA</span>
          <span className="logo-fun">FUN</span>
          <span className="logo-zone">ZONE</span>
          <span className="logo-com">.com</span>
        </div>
        <div className="mfz-header-info">
          <span className="mfz-tagline">Play Games. Forget Your Problems. We Will Handle The Rest.</span>
          <span className="mfz-counter">
            ONLINE NOW: <b>47,293</b> gamers &nbsp;|&nbsp; Games: <b>{MFZ_GAMES.length}</b> &nbsp;|&nbsp;
            Best: <b>Desktop Tower Defense</b> (4.9 stars)
          </span>
        </div>
        <div
          className="mfz-header-ad-box blinking"
          style={{ background: randomAd.color }}
        >
          {randomAd.text}
        </div>
      </div>
      <div className="mfz-nav">
        <span className="mfz-nav-item" onClick={goHome}>Home</span>
        {MFZ_CATEGORIES.filter(c => c !== 'All').map(cat => (
          <span
            key={cat}
            className="mfz-nav-item"
            onClick={() => { setActiveCategory(cat); goHome(); }}
          >
            {cat}
          </span>
        ))}
        <span className="mfz-nav-item" onClick={() => { setSortBy('rating'); goHome(); }}>Top Rated</span>
        <span className="mfz-nav-item" onClick={() => { setSortBy('newest'); goHome(); }}>New Games</span>
        <span className="mfz-nav-item">Submit</span>
        <span className="mfz-nav-item">Forum</span>
      </div>
    </div>
  );

  /* ------------------------------------------------------------------ */
  /* GAME VIEW                                                            */
  /* ------------------------------------------------------------------ */
  if (view === 'game' && selectedGame) {
    return (
      <div className="megafunzone-page">
        <Header />

        <div className="mfz-breadcrumb">
          <span className="mfz-bc-link" onClick={goHome}>MegaFunZone.com</span>
          {' > '}
          <span
            className="mfz-bc-link"
            onClick={() => { setActiveCategory(selectedGame.category); goHome(); }}
          >
            {selectedGame.category}
          </span>
          {' > '}
          <span>{selectedGame.name}</span>
        </div>

        <div className="mfz-game-layout">
          <div className="mfz-game-main">
            <div className="mfz-game-title-bar">
              <h2>{selectedGame.name}</h2>
              <div className="mfz-game-meta-bar">
                <StarRating rating={selectedGame.rating} />
                <span>{selectedGame.rating}/5 ({selectedGame.votes.toLocaleString()} votes)</span>
                <span>|</span>
                <span>{selectedGame.plays} plays</span>
                <span>|</span>
                <span>{selectedGame.category}</span>
                <span>|</span>
                <span>{selectedGame.year}</span>
              </div>
            </div>

            <div className="mfz-player-container">
              {selectedGame.swfPath ? (
                <RufflePlayer swfUrl={selectedGame.swfPath} gameName={selectedGame.name} />
              ) : (
                <div className="mfz-coming-soon">
                  <div className="mfz-cs-inner">
                    <div className="mfz-flash-icon">F</div>
                    <h3>{selectedGame.name}</h3>
                    <p>This game requires Adobe Flash Player 9 or higher.</p>
                    <div className="mfz-loading-bar-wrap">
                      <div className="mfz-loading-bar-inner" />
                    </div>
                    <p className="mfz-cs-error">
                      Flash content unavailable — SWF file not found on server.<br />
                      <a href="#">Download Flash Player</a> &nbsp;|&nbsp; <a href="#">Report broken game</a>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mfz-below-player">
              <div className="mfz-player-actions">
                <button className="mfz-action-btn">Fullscreen</button>
                <button className="mfz-action-btn">Add to Favorites</button>
                <button className="mfz-action-btn">Report Problem</button>
                <button className="mfz-action-btn" onClick={goHome}>&lt;&lt; Back to Games</button>
              </div>
              <div className="mfz-tags">
                Tags:{' '}
                {selectedGame.tags.map(t => (
                  <span key={t} className="mfz-tag">{t}</span>
                ))}
              </div>
            </div>

            <div className="mfz-comment-section">
              <div className="mfz-section-header">User Comments ({gameComments.length})</div>
              <div className="mfz-comments">
                {gameComments.map((c, i) => (
                  <div key={i} className="mfz-comment">
                    <div className="mfz-comment-header">
                      <span className="mfz-comment-user">{c.user}</span>
                      <span className="mfz-comment-time">{c.time}</span>
                    </div>
                    <p className="mfz-comment-text">{c.text}</p>
                  </div>
                ))}
              </div>
              <div className="mfz-add-comment">
                <input
                  type="text"
                  placeholder="Post a comment... (you must be registered and logged in)"
                  className="mfz-comment-input"
                  readOnly
                />
                <button className="mfz-submit-btn">Post</button>
              </div>
            </div>
          </div>

          <div className="mfz-game-sidebar">
            <div className="mfz-sidebar-box">
              <div className="mfz-section-header">Game Info</div>
              <div className="mfz-game-info">
                <p>{selectedGame.description}</p>
                <table className="mfz-info-table">
                  <tbody>
                    <tr><td>Category:</td><td>{selectedGame.category}</td></tr>
                    <tr><td>Year:</td><td>{selectedGame.year}</td></tr>
                    <tr><td>Plays:</td><td>{selectedGame.plays}</td></tr>
                    <tr><td>Rating:</td><td><StarRating rating={selectedGame.rating} /></td></tr>
                    <tr><td>Votes:</td><td>{selectedGame.votes.toLocaleString()}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mfz-sidebar-box">
              <div className="mfz-section-header">How to Play</div>
              <p className="mfz-instructions">{selectedGame.instructions}</p>
            </div>

            {relatedGames.length > 0 && (
              <div className="mfz-sidebar-box">
                <div className="mfz-section-header">More {selectedGame.category} Games</div>
                <div className="mfz-related-games">
                  {relatedGames.map(g => (
                    <div key={g.id} className="mfz-related-item" onClick={() => openGame(g)}>
                      <div className="mfz-related-thumb" style={{ background: g.thumbBg }}>
                        <span>{g.thumbLabel}</span>
                      </div>
                      <div className="mfz-related-info">
                        <span className="mfz-related-name">{g.name}</span>
                        <StarRating rating={g.rating} />
                        <span className="mfz-related-plays">{g.plays} plays</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mfz-sidebar-box mfz-ad-box">
              <div className="mfz-punch-ad blinking">
                <div className="mfz-punch-target">PUNCH</div>
                <p>PUNCH THE MONKEY</p>
                <p>AND WIN $1,000!</p>
                <button>Click Here!!</button>
              </div>
            </div>

            <div className="mfz-sidebar-box">
              <div className="mfz-section-header">Rate This Game</div>
              <div className="mfz-rate-box">
                <p>Current rating: <StarRating rating={selectedGame.rating} /></p>
                <p>{selectedGame.rating}/5 from {selectedGame.votes.toLocaleString()} votes</p>
                <div className="mfz-rate-stars">
                  {[1, 2, 3, 4, 5].map(n => (
                    <span key={n} className="mfz-rate-star">★</span>
                  ))}
                </div>
                <p className="mfz-rate-hint">(click a star to rate)</p>
              </div>
            </div>
          </div>
        </div>

        <MFZFooter onNavigate={onNavigate} />
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* HOME VIEW                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <div className="megafunzone-page">
      <Header />

      <div className="mfz-ticker">
        <span className="mfz-ticker-label">HOT</span>
        <marquee scrollamount="3">
          Bloons passes 12 MILLION plays — still the best puzzle game online! &nbsp;||&nbsp;
          Staff Pick: Desktop Tower Defense — build the perfect maze and stop the creeps &nbsp;||&nbsp;
          Line Rider Challenge: Make him do a full loop-de-loop and post your screenshot! &nbsp;||&nbsp;
          WARNING: Motherload may cause you to lose track of time entirely &nbsp;||&nbsp;
          Desktop Tower Defense: Can you survive all 50 waves on Hard mode?
        </marquee>
      </div>

      <div className="mfz-content">

        {/* LEFT SIDEBAR */}
        <div className="mfz-sidebar-left">

          <div className="mfz-sidebar-box">
            <div className="mfz-section-header">Categories</div>
            <ul className="mfz-cat-list">
              {MFZ_CATEGORIES.map(cat => (
                <li
                  key={cat}
                  className={activeCategory === cat ? 'active' : ''}
                  onClick={() => setActiveCategory(cat)}
                >
                  <span>{cat === 'All' ? 'All Games' : cat}</span>
                  <span className="mfz-cat-count">
                    ({cat === 'All' ? MFZ_GAMES.length : MFZ_GAMES.filter(g => g.category === cat).length})
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {featuredGame && (
            <div className="mfz-sidebar-box">
              <div className="mfz-section-header">Featured Game</div>
              <div className="mfz-featured-mini" onClick={() => openGame(featuredGame)}>
                <div className="mfz-featured-mini-thumb" style={{ background: featuredGame.thumbBg }}>
                  <span>{featuredGame.thumbLabel}</span>
                </div>
                <div className="mfz-featured-mini-info">
                  <span className="mfz-featured-mini-name">{featuredGame.name}</span>
                  <StarRating rating={featuredGame.rating} />
                  <span className="mfz-featured-mini-plays">{featuredGame.plays} plays</span>
                  <button className="mfz-play-btn-small">PLAY NOW</button>
                </div>
              </div>
            </div>
          )}

          {dailyGame && (
            <div className="mfz-sidebar-box">
              <div className="mfz-section-header">Daily Challenge</div>
              <div className="mfz-daily">
                <p><b>Today:</b> {dailyGame.name}</p>
                <p className="mfz-daily-task">Survive all 50 waves on Hard!</p>
                <p className="mfz-daily-prize">Prize: 5,000 FunCoins</p>
                <button className="mfz-play-btn-small" onClick={() => openGame(dailyGame)}>
                  ACCEPT CHALLENGE
                </button>
              </div>
            </div>
          )}

          <div className="mfz-sidebar-box mfz-ad-box">
            <div className="mfz-banner-ad blinking" style={{ background: '#CC0000' }}>
              <p>YOU ARE VISITOR</p>
              <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFFF00' }}>1,000,000!</p>
              <p>CLICK TO CLAIM</p>
              <p>YOUR FREE PRIZE!</p>
              <button>Claim Now!!</button>
            </div>
          </div>

          <div className="mfz-sidebar-box">
            <div className="mfz-section-header">Quick Links</div>
            <ul className="mfz-quick-links">
              <li><a href="#">Submit a Game</a></li>
              <li><a href="#">Rate Games</a></li>
              <li><a href="#">High Scores</a></li>
              <li><a href="#">Forums</a></li>
              <li><a href="#">Register FREE</a></li>
              <li><a href="#">Report a Bug</a></li>
            </ul>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="mfz-main">

          {featuredGame && (
            <div className="mfz-featured-banner" onClick={() => openGame(featuredGame)}>
              <div className="mfz-featured-thumb-lg" style={{ background: featuredGame.thumbBg }}>
                <span className="mfz-featured-label-lg">{featuredGame.thumbLabel}</span>
              </div>
              <div className="mfz-featured-content">
                <div className="mfz-featured-badge">STAFF PICK OF THE WEEK</div>
                <h3 className="mfz-featured-title">{featuredGame.name}</h3>
                <p className="mfz-featured-desc">{featuredGame.description}</p>
                <div className="mfz-featured-stats">
                  <StarRating rating={featuredGame.rating} />
                  &nbsp;{featuredGame.rating}/5 &nbsp;|&nbsp; {featuredGame.plays} plays
                </div>
                <button className="mfz-featured-play-btn">PLAY NOW &gt;&gt;</button>
              </div>
            </div>
          )}

          <div className="mfz-games-bar">
            <h2 className="mfz-games-title">
              {activeCategory === 'All' ? 'All Games' : `${activeCategory} Games`}
              <span className="mfz-game-count"> ({filteredGames.length} games)</span>
            </h2>
            <div className="mfz-sort">
              Sort by:&nbsp;
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest First</option>
              </select>
            </div>
          </div>

          <div className="mfz-games-grid">
            {filteredGames.map(game => (
              <div
                key={game.id}
                className="mfz-game-card"
                onClick={() => openGame(game)}
                data-testid={`game-card-${game.id}`}
              >
                <GameThumbnail game={game} />
                <div className="mfz-card-info">
                  <div className="mfz-card-name">{game.name}</div>
                  <div className="mfz-card-rating"><StarRating rating={game.rating} /></div>
                  <div className="mfz-card-plays">{game.plays} plays</div>
                  <div className="mfz-card-cat">{game.category}</div>
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* RIGHT SIDEBAR */}
        <div className="mfz-sidebar-right">

          <div className="mfz-sidebar-box">
            <div className="mfz-section-header">Top Rated</div>
            <ol className="mfz-top-rated">
              {topRated.map((g, i) => (
                <li key={g.id} onClick={() => openGame(g)}>
                  <span className="mfz-tr-num">{i + 1}.</span>
                  <span className="mfz-tr-name">{g.name}</span>
                  <StarRating rating={g.rating} />
                </li>
              ))}
            </ol>
          </div>

          <div className="mfz-sidebar-box">
            <div className="mfz-section-header">Top Players</div>
            <ol className="mfz-leaderboard">
              <li><span>xXGamerGodXx</span><b>999,999</b></li>
              <li><span>ProPlayer2007</span><b>875,432</b></li>
              <li><span>FlashMaster</span><b>756,123</b></li>
              <li><span>ArcadeKing</span><b>654,321</b></li>
              <li><span>GameQueen99</span><b>543,210</b></li>
            </ol>
          </div>

          <div className="mfz-sidebar-box mfz-ad-box">
            <div className="mfz-tall-ad blinking">
              <p>CONGRATULATIONS!</p>
              <p>You have been</p>
              <p>selected to receive</p>
              <p style={{ fontSize: '16px', color: '#FFFF00', fontWeight: 'bold' }}>A FREE PRIZE</p>
              <p>Click below to claim</p>
              <button>CONTINUE &gt;&gt;</button>
            </div>
          </div>

          <div className="mfz-sidebar-box">
            <div className="mfz-section-header">Shoutbox</div>
            <div className="mfz-shoutbox">
              <div className="mfz-shouts">
                <p><b>cooldude99:</b> DTD maze strategy = zig zag all the way</p>
                <p><b>gamergirl:</b> stuck on bloons level 47 forever lol</p>
                <p><b>flashfan:</b> line rider loop took me 2 hours to build</p>
                <p><b>xX_pr0:</b> DTD is the BEST game on this site no contest</p>
                <p><b>drill_king:</b> motherload gets SO deep. dont run out of fuel</p>
                <p><b>dartmaster:</b> 1 dart, 32 bloons. screenshot on my profile</p>
              </div>
              <input type="text" placeholder="Shout something..." className="mfz-shout-input" readOnly />
              <button className="mfz-shout-btn">Shout!</button>
            </div>
          </div>

          <div className="mfz-sidebar-box">
            <div className="mfz-section-header">Go Premium</div>
            <div className="mfz-sponsor-box">
              <p><b>Remove banner ads!</b></p>
              <p>Join Premium and get:</p>
              <ul>
                <li>No Banner Ads!</li>
                <li>Save High Scores!</li>
                <li>Custom Avatar!</li>
                <li>Forum Access!</li>
                <li>Beta Game Access!</li>
              </ul>
              <button className="mfz-premium-btn">Join PREMIUM — $4.99/mo</button>
            </div>
          </div>

        </div>
      </div>

      <MFZFooter onNavigate={onNavigate} />
    </div>
  );
};

export default MegaFunZonePage;
